import {round} from 'lodash-es';
import type {FoundValue, FoundValueOrigin} from './foundValues';

export interface RawRoom {
    type: string;
    isAddition: boolean;
    dimensions: Array<[number, number] | number>;
}

export interface RawPart {
    name: string;
    rooms: RawRoom[];
}

/**
 * What a drawing tells of a part of the building beyond its floor area
 */
export interface BuildingPartNote {
    name: string;
    /** The total area written in the drawing */
    statedArea: number | null;
    roomLabels: Array<string | null>;
}

export interface FoundRoom {
    room: RawRoom;
    area: number;
    /** The label of the room in the drawing, e.g. "1.04 Pokoj" */
    label: string | null;
    /** Probably the same room as one of the form or of another source with a part of the same name */
    duplicate: 'form' | 'source' | null;
}

/**
 * A part of the building, e.g. a floor, one source found
 */
export interface FoundPart {
    key: string;
    name: string;
    rooms: FoundRoom[];
    area: number;
    /** The total area the drawing states for the part, to check the area read */
    statedArea: number | null;
    origins: FoundValueOrigin[];
    isNew: boolean;
    /** The form has the same part already */
    inForm: boolean;
}

/** Where the rooms of a found part go: a new part, a part of the form by its index, or nowhere */
export type PartTarget = 'new' | 'skip' | `add:${number}` | `replace:${number}`;

export interface PartChoice {
    target: PartTarget;
    /** Name of the new part, the new parts of the same name are one part */
    name: string;
    /** The rooms taken over, by their index */
    rooms: boolean[];
}

export function isPartList(value: any): value is RawPart[] {
    return Array.isArray(value) && value.every(part => part !== null && typeof part === 'object'
        && typeof part.name === 'string' && Array.isArray(part.rooms));
}

export function roomArea(room: RawRoom): number {
    return round(room.dimensions.reduce<number>(
        (sum, dimension) => sum + (Array.isArray(dimension) ? dimension[0] * dimension[1] : dimension),
        0,
    ), 2);
}

export function partArea(part: RawPart): number {
    return round(part.rooms.reduce((sum, room) => sum + roomArea(room), 0), 2);
}

/** Names of floors are written differently, e.g. "1.NP" and "1. NP" */
export function sameName(a: string, b: string): boolean {
    return a.replace(/\s+/g, '').toLowerCase() === b.replace(/\s+/g, '').toLowerCase();
}

/** The rooms of a drawing have no labels, a room is known by its type and area */
function sameRoom(a: RawRoom, b: RawRoom): boolean {
    return a.type === b.type && roomArea(a) === roomArea(b);
}

function sameRooms(a: RawRoom[], b: RawRoom[]): boolean {
    return a.length === b.length && a.every((room, index) => sameRoom(room, b[index]));
}

/** Rooms of the same type and area are different rooms when the drawings label them differently */
function differentLabels(a: FoundRoom, b: FoundRoom): boolean {
    return a.label !== null && b.label !== null && a.label !== b.label;
}

/**
 * The note of a part by its position, a note of a part read differently than stored is no use
 */
function findNote(origins: FoundValueOrigin[], part: RawPart, partIndex: number): BuildingPartNote | null {
    for (const origin of origins) {
        const note = origin.buildingPartNotes?.[partIndex];

        if (note !== undefined && sameName(note.name, part.name) && note.roomLabels.length === part.rooms.length) {
            return note;
        }
    }

    return null;
}

/**
 * The parts of the building the sources found, one per part of every value
 */
export function buildFoundParts(values: FoundValue[], current: any): FoundPart[] {
    const currentParts = isPartList(current) ? current : [];
    const parts: FoundPart[] = [];
    // the value of a part, the parts of one value are no duplicates of each other
    const valueIndexes: number[] = [];

    values.forEach((value, valueIndex) => {
        if (!value.valid || !isPartList(value.value)) {
            return;
        }

        value.value.forEach((part, partIndex) => {
            const note = findNote(value.origins, part, partIndex);

            valueIndexes.push(valueIndex);
            parts.push({
                key: `${valueIndex}-${partIndex}`,
                name: part.name,
                rooms: part.rooms.map((room, roomIndex) => ({
                    room,
                    area: roomArea(room),
                    label: note?.roomLabels[roomIndex] ?? null,
                    duplicate: null,
                })),
                area: partArea(part),
                statedArea: note?.statedArea ?? null,
                origins: value.origins,
                isNew: value.new,
                inForm: currentParts.some(currentPart => sameName(currentPart.name, part.name)
                    && sameRooms(currentPart.rooms, part.rooms)),
            });
        });
    });

    // two sources of one floor, e.g. a drawing and an advert, would count its rooms twice when merged
    parts.forEach((part, index) => {
        for (const found of part.rooms) {
            if (currentParts.some(currentPart => sameName(currentPart.name, part.name)
                && currentPart.rooms.some(room => sameRoom(room, found.room)))) {
                found.duplicate = 'form';
            } else if (parts.slice(0, index).some((earlier, earlierIndex) => valueIndexes[earlierIndex] !== valueIndexes[index]
                && sameName(earlier.name, part.name)
                && earlier.rooms.some(room => sameRoom(room.room, found.room) && !differentLabels(room, found)))) {
                found.duplicate = 'source';
            }
        }
    });

    return parts;
}

/** The total of a drawing is rounded to tenths of m² */
const STATED_AREA_TOLERANCE = 0.1;

/**
 * The area read compared with the total the drawing states, null for a drawing that states none
 *
 * @return difference the area read minus the stated one
 */
export function compareStatedArea(part: FoundPart): {matches: boolean, difference: number} | null {
    if (part.statedArea === null) {
        return null;
    }

    const difference = round(part.area - part.statedArea, 2);

    return {matches: Math.abs(difference) <= STATED_AREA_TOLERANCE, difference};
}

/**
 * A part goes to the part of the form of the same name, otherwise to a new part; the new parts of the same name
 * are merged then; only the parts of the sources not seen yet are taken over at first, the probable duplicates
 * of rooms are left out
 */
export function defaultPartChoice(part: FoundPart, current: any): PartChoice {
    const index = isPartList(current) ? current.findIndex(currentPart => sameName(currentPart.name, part.name)) : -1;

    let target: PartTarget = index >= 0 ? `add:${index}` : 'new';

    if (!part.isNew || part.inForm) {
        target = 'skip';
    }

    return {target, name: part.name, rooms: part.rooms.map(room => room.duplicate === null)};
}

function isTaken(choice: PartChoice | undefined): choice is PartChoice {
    return choice !== undefined && choice.target !== 'skip' && choice.rooms.some(Boolean);
}

/**
 * The floor area of the form with the chosen parts, null when nothing is chosen
 */
export function composeFloorArea(current: any, parts: FoundPart[], choices: Record<string, PartChoice>): RawPart[] | null {
    const taken = parts.filter(part => isTaken(choices[part.key]));

    if (taken.length === 0) {
        return null;
    }

    // a floor area given by its sum only is replaced by the parts
    const result: RawPart[] = (isPartList(current) ? current : []).map(part => ({name: part.name, rooms: [...part.rooms]}));

    for (const part of taken) {
        const target = choices[part.key].target;

        if (target.startsWith('replace:')) {
            result[Number(target.split(':')[1])].rooms = [];
        }
    }

    const newParts: RawPart[] = [];

    for (const part of taken) {
        const choice = choices[part.key];
        const rooms = part.rooms.filter((_, index) => choice.rooms[index]).map(found => found.room);

        if (choice.target !== 'new') {
            result[Number(choice.target.split(':')[1])]?.rooms.push(...rooms);
            continue;
        }

        const name = choice.name.trim() !== '' ? choice.name.trim() : part.name;
        const sameNamed = newParts.find(newPart => sameName(newPart.name, name));

        if (sameNamed !== undefined) {
            sameNamed.rooms.push(...rooms);
        } else {
            const newPart = {name, rooms: [...rooms]};
            newParts.push(newPart);
            result.push(newPart);
        }
    }

    return result.filter(part => part.rooms.length > 0);
}
